import { useMemo, useState } from "react";
import { X } from "@phosphor-icons/react";

// Launch Media (SM-5): a file's tags as chips, plus a box that suggests the account's tags or
// offers to create a new one. Arrow keys and Enter pick; Escape closes.
export const TagPicker = ({ tags, all, onAdd, onRemove }: { tags: string[]; all: { name: string; count: number }[]; onAdd: (name: string) => void; onRemove: (name: string) => void }) => {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const q = text.trim();
  const options = useMemo(() => {
    const pool = all.filter((t) => !tags.some((x) => x.toLowerCase() === t.name.toLowerCase()) && t.name.toLowerCase().includes(q.toLowerCase()));
    const out = pool.map((t) => ({ name: t.name, label: t.name, note: `${t.count} file${t.count === 1 ? "" : "s"}` }));
    if (q && !all.some((t) => t.name.toLowerCase() === q.toLowerCase())) out.push({ name: q, label: `Create “${q}”`, note: "new tag" });
    return out;
  }, [all, tags, q]);
  const pick = (name?: string) => { if (!name) return; onAdd(name); setText(""); setHi(0); };
  return (
    <div className="flex flex-col gap-2">
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-black/5 dark:bg-white/10">
              {t}<button type="button" onClick={() => onRemove(t)} aria-label={`Remove the tag ${t}`} className="opacity-60 hover:opacity-100"><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          value={text}
          onChange={(e) => { setText(e.target.value); setHi(0); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { setHi((n) => Math.min(options.length - 1, n + 1)); e.preventDefault(); }
            else if (e.key === "ArrowUp") { setHi((n) => Math.max(0, n - 1)); e.preventDefault(); }
            else if (e.key === "Enter") { e.preventDefault(); pick(options[hi]?.name); }
            else if (e.key === "Escape") setOpen(false);
          }}
          placeholder="Add a tag: pick one or type a new one"
          aria-label="Add a tag"
          className="w-full rounded-lg border border-black/10 dark:border-white/20 bg-transparent px-2.5 py-1.5 text-base md:text-sm"
        />
        {open && options.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 z-20 max-h-56 overflow-y-auto rounded-lg border border-black/10 dark:border-white/20 bg-white dark:bg-[#2c2c2c] shadow-lg p-1" role="listbox">
            {options.map((o, i) => (
              <button key={o.label} type="button" role="option" aria-selected={i === hi} onMouseDown={(e) => { e.preventDefault(); pick(o.name); }}
                className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${i === hi ? "bg-blue-50 dark:bg-white/10" : "hover:bg-black/5 dark:hover:bg-white/5"}`}>
                <span className="flex-1 truncate">{o.label}</span><span className="text-[11px] text-black/40 dark:text-white/40">{o.note}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
